import React, { useEffect } from 'react';
import { Button, Form, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import toast from 'react-hot-toast';
import AdminService from '../../services/admin.service';

interface IInstantExportModal {
    show: boolean;
    onHide: () => void;
}

// Data formats available for instant export. Each entry maps to a backend handler.
const DATA_FORMATS = [
    { value: 'haryana-live-140626', label: 'Haryana (Live Data format - 140626)' },
    { value: 'live-verification-images', label: 'Export Live Verification Images' },
    { value: 'attendance-status', label: 'Attendance Status (from Excel)' },
];

export default function InstantExportModal(props: IInstantExportModal) {
    const [dataFormat, setDataFormat] = React.useState<any>(null);
    const [exams, setExams] = React.useState<any[]>([]);
    const [shifts, setShifts] = React.useState<any[]>([]);
    const [selectedExam, setSelectedExam] = React.useState<any>(null);
    const [selectedShift, setSelectedShift] = React.useState<any>(null);
    const [rollNos, setRollNos] = React.useState<string[]>([]);
    const [rollNoFileName, setRollNoFileName] = React.useState<string>('');
    const [attendanceFile, setAttendanceFile] = React.useState<File | null>(null);
    const [isExporting, setIsExporting] = React.useState<boolean>(false);
    // Live-images job progress: null when not running.
    const [progress, setProgress] = React.useState<{ processed: number; total: number } | null>(null);
    // Elapsed seconds while a long export (attendance status) is being built.
    const [elapsed, setElapsed] = React.useState<number>(0);
    const timerRef = React.useRef<any>(null);

    // Throughput used for the estimated time hint on the attendance-status export.
    const RECORDS_PER_SECOND = 100;

    const startTimer = () => {
        setElapsed(0);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    };
    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setElapsed(0);
    };

    React.useEffect(() => {
        // Clean up the timer if the component unmounts mid-export.
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const formatDuration = (totalSeconds: number) => {
        const m = Math.floor(totalSeconds / 60);
        const s = totalSeconds % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    const needsRollNos = dataFormat?.value === 'live-verification-images';
    const needsExcel = dataFormat?.value === 'attendance-status';

    // Parse an uploaded roll-number CSV. Reads the first column of each row,
    // skips a header row named "rollnumber"/"roll number"/"rollno".
    const handleRollNoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setRollNoFileName(file.name);

        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || '');
            const parsed = text
                .split(/\r?\n/)
                .map((line) => line.split(',')[0]?.trim())
                .filter((v): v is string => !!v && v.length > 0)
                .filter((v) => !/^roll\s*(number|no)$/i.test(v));

            const unique = Array.from(new Set(parsed));
            setRollNos(unique);
            if (unique.length === 0) {
                toast.error('No roll numbers found in the file');
            } else {
                toast.success(`${unique.length} roll number(s) loaded`);
            }
        };
        reader.onerror = () => toast.error('Failed to read file');
        reader.readAsText(file);
    };

    const getExams = async () => {
        try {
            const res = await AdminService.getAllExamName();
            if (res.status === 200) {
                setExams(
                    res.data.data.examName.map((e: any) => ({ value: e._id, label: e.name }))
                );
            }
        } catch (err) {
            console.log(err);
            toast.error('Failed to load exams');
        }
    };

    const getShifts = async (examId: string) => {
        try {
            const res = await AdminService.getShiftData(examId);
            if (res.status === 200) {
                setShifts(
                    res.data.data.shifts.map((s: any) => ({ value: s._id, label: s.shiftName }))
                );
            }
        } catch (err) {
            console.log(err);
            toast.error('Failed to load shifts');
        }
    };

    useEffect(() => {
        if (props.show) {
            getExams();
        }
    }, [props.show]);

    useEffect(() => {
        setSelectedShift(null);
        setShifts([]);
        if (selectedExam?.value) {
            getShifts(selectedExam.value);
        }
    }, [selectedExam]);

    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    // Trigger a browser file download from a blob/binary response body.
    const triggerBlobDownload = (data: any, fileName: string, mimeType: string) => {
        const blob = new Blob([data], { type: mimeType });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    };

    // Start the live-images job, poll progress, then download the finished zip.
    const runLiveImagesExport = async () => {
        const startRes = await AdminService.startInstantExportLiveImages(
            selectedExam.value,
            rollNos
        );
        const jobId = startRes.data?.jobId;
        const total = startRes.data?.total || rollNos.length;
        if (!jobId) {
            throw new Error('Failed to start export');
        }

        setProgress({ processed: 0, total });

        // Poll until the job completes or fails.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            await sleep(1500);
            const statusRes = await AdminService.getInstantExportLiveImagesStatus(jobId);
            const { status, processed, total: t } = statusRes.data || {};
            setProgress({ processed: processed || 0, total: t || total });

            if (status === 'completed') break;
            if (status === 'failed') {
                throw new Error(statusRes.data?.error || 'Export failed while processing');
            }
        }

        const downloadRes = await AdminService.downloadInstantExportLiveImages(jobId);
        const link = downloadRes.data?.link;
        if (!link) {
            throw new Error('Export file link not available');
        }
        // Open the S3 link to start the download.
        window.open(link, '_blank');

        setProgress(null);
        toast.success('Export ready — download started');
        props.onHide();
    };

    const handleExport = async () => {
        if (!dataFormat) {
            toast.error('Please select a data format');
            return;
        }
        if (!selectedExam) {
            toast.error('Please select an exam');
            return;
        }
        // Shift is required only for formats that operate on a single shift.
        if (!needsRollNos && !needsExcel && !selectedShift) {
            toast.error('Please select a shift');
            return;
        }

        if (needsRollNos && rollNos.length === 0) {
            toast.error('Please upload a roll number CSV file');
            return;
        }

        if (needsExcel && !attendanceFile) {
            toast.error('Please upload the attendance Excel file');
            return;
        }

        setIsExporting(true);
        try {
            // The live-images export is a long-running job (can be 10K+ images), so
            // it runs as a background job with polled progress before downloading.
            if (dataFormat.value === 'live-verification-images') {
                await runLiveImagesExport();
                return;
            }

            let res;
            let fileName: string;
            let mimeType: string;

            // Route the export based on the selected data format.
            if (dataFormat.value === 'haryana-live-140626') {
                res = await AdminService.instantExportHaryanaLive(
                    selectedExam.value,
                    selectedShift.value
                );
                fileName = `${selectedExam.label}-${selectedShift.label}-attendance.csv`;
                mimeType = 'text/csv;charset=utf-8;';
            } else if (dataFormat.value === 'attendance-status') {
                // Long synchronous build — show a running timer while it processes.
                startTimer();
                res = await AdminService.instantExportAttendanceStatus(
                    selectedExam.value,
                    attendanceFile as File
                );
                stopTimer();
                fileName = `${selectedExam.label}-attendance-status.csv`;
                mimeType = 'text/csv;charset=utf-8;';
            } else {
                toast.error('Unsupported data format');
                return;
            }

            triggerBlobDownload(res.data, fileName, mimeType);
            toast.success('Export downloaded successfully');
            props.onHide();
        } catch (err: any) {
            console.log(err);
            // With responseType "blob", the error body is also a Blob — read it as text.
            let message = 'Export failed, please try again';
            try {
                const data = err?.response?.data;
                if (data instanceof Blob) {
                    const text = await data.text();
                    try {
                        const parsed = JSON.parse(text);
                        message = parsed?.message || text || message;
                    } catch {
                        message = text || message;
                    }
                } else if (typeof data === 'string' && data) {
                    message = data;
                } else if (data?.message) {
                    message = data.message;
                } else if (err?.message) {
                    message = err.message;
                }
            } catch {
                // keep default message
            }
            toast.error(message);
        } finally {
            stopTimer();
            setProgress(null);
            setIsExporting(false);
        }
    };

    // Download a sample CSV showing the required upload format (roll number per row).
    const downloadSampleRollNos = () => {
        const sample = ['RollNumber', '100000001', '100000002', '100000003'].join('\r\n');
        const blob = new Blob(['﻿' + sample], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'roll-numbers-sample.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    };

    const handleHide = () => {
        if (isExporting) return;
        setDataFormat(null);
        setSelectedExam(null);
        setSelectedShift(null);
        setShifts([]);
        setRollNos([]);
        setRollNoFileName('');
        setAttendanceFile(null);
        setProgress(null);
        props.onHide();
    };

    return (
        <Modal show={props.show} onHide={handleHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Instant Export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Type of Data</Form.Label>
                    <Select
                        options={DATA_FORMATS}
                        value={dataFormat}
                        onChange={(e: any) => setDataFormat(e)}
                        placeholder="Select data format"
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Exam</Form.Label>
                    <Select
                        options={exams}
                        value={selectedExam}
                        onChange={(e: any) => setSelectedExam(e)}
                        placeholder="Select exam"
                    />
                </Form.Group>

                {!needsRollNos && !needsExcel && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Shift</Form.Label>
                        <Select
                            options={shifts}
                            value={selectedShift}
                            onChange={(e: any) => setSelectedShift(e)}
                            placeholder="Select shift"
                            isDisabled={!selectedExam}
                        />
                    </Form.Group>
                )}

                {needsRollNos && (
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <Form.Label className="fw-bold mb-0">Roll Number CSV</Form.Label>
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={downloadSampleRollNos}
                            >
                                Download required format
                            </Button>
                        </div>
                        <Form.Control
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleRollNoFile}
                            className="mt-1"
                        />
                        <Form.Text className="text-muted">
                            {rollNoFileName
                                ? `${rollNoFileName} — ${rollNos.length} roll number(s) loaded.`
                                : 'Upload a CSV with one roll number per row. Shift is detected automatically.'}
                        </Form.Text>
                    </Form.Group>
                )}

                {needsExcel && (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Attendance Excel</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            onChange={(e: any) => setAttendanceFile(e.target.files?.[0] || null)}
                            className="mt-1"
                        />
                        <Form.Text className="text-muted">
                            {attendanceFile
                                ? `${attendanceFile.name} selected. The "Attendance Status" column will be filled and returned as CSV.`
                                : 'Upload the Excel with a "ROLL NUMBER" column. Statuses are matched by exam + roll number. Result is returned as CSV.'}
                        </Form.Text>
                    </Form.Group>
                )}

                {needsExcel && !isExporting && (
                    <div className="alert alert-info py-2 px-3 xcn-text-12 mb-3">
                        This can take a while for large files — it processes about{' '}
                        {RECORDS_PER_SECOND} records/second. For example, ~30,000 records take
                        around {formatDuration(Math.ceil(30000 / RECORDS_PER_SECOND))}. Please keep
                        this window open until the download starts.
                    </div>
                )}

                {needsExcel && isExporting && (
                    <div className="alert alert-warning py-2 px-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="xcn-text-12">
                                Processing… (~{RECORDS_PER_SECOND} records/sec)
                            </span>
                            <span className="fw-bold">{formatDuration(elapsed)}</span>
                        </div>
                        <div className="xcn-text-12 text-muted mt-1">
                            Please don't close this window. The download will begin automatically.
                        </div>
                    </div>
                )}

                {progress && (
                    <div className="mt-3">
                        <div className="d-flex justify-content-between xcn-text-12 mb-1">
                            <span>Processing images...</span>
                            <span>
                                {progress.processed} / {progress.total}
                            </span>
                        </div>
                        <ProgressBar
                            now={progress.total ? (progress.processed / progress.total) * 100 : 0}
                            label={`${progress.total
                                ? Math.round((progress.processed / progress.total) * 100)
                                : 0}%`}
                            animated
                            striped
                        />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={handleHide} disabled={isExporting}>
                    Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={handleExport} disabled={isExporting}>
                    {isExporting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Exporting...
                        </>
                    ) : (
                        'Export Now'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
